// ---------------------------------------------------------------------------
// useNativeWheelScroll
// ---------------------------------------------------------------------------
// Intercepta o evento wheel de um elemento rolável com listener nativo
// (passive: false) e aplica o scroll manualmente, com stopPropagation.
//
// Motivação: scrollbars transparentes (overflow: overlay) fazem o browser
// ser instável no hit-testing do wheel, às vezes propagando o evento
// ao ancestral em vez de rolar o próprio elemento. Esse hook consome o
// evento deterministicamente, mas deixa passar quando está no "overscroll"
// (topo ou fundo) — assim o scroll da página segue natural quando o
// elemento não tem mais para onde rolar.
//
// Uso:
//   const scrollRef = useNativeWheelScroll();
//   return <div ref={scrollRef} className={...}>...</div>;
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

export default function useNativeWheelScroll() {
  const [el, setEl] = useState(null);
  const ref = useCallback((node) => setEl(node), []);

  useEffect(() => {
    if (!el) return undefined;
    const handler = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const delta = e.deltaY;
      const canScrollUp = delta < 0 && scrollTop > 0;
      const canScrollDown =
        delta > 0 && scrollTop < scrollHeight - clientHeight - 0.5;
      if (!canScrollUp && !canScrollDown) return; // overscroll — propaga
      e.preventDefault();
      e.stopPropagation();
      el.scrollTop += delta;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [el]);

  return ref;
}
